import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowRight, Building2, Filter, LayoutGrid, Shuffle, ShieldOff,
  CheckCircle2, ArrowLeftRight
} from 'lucide-react';
import { corporatePageContent } from '../data/mock';

const iconMap = {
  Filter, LayoutGrid, Shuffle, ShieldOff
};

const CorporatePage = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-slate-50 to-white pt-16 pb-24">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-10 w-72 h-72 bg-[#1e3a5f]/5 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-20 w-80 h-80 bg-emerald-500/5 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-[#1e3a5f]/10 text-[#1e3a5f] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Building2 className="w-4 h-4" />
                <span>For Corporate R&D Teams</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-[#0f1f2e] leading-tight mb-6">
                {corporatePageContent.hero.headline}
              </h1>
              
              <p className="text-lg text-slate-600 leading-relaxed mb-10">
                {corporatePageContent.hero.subheadline}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/request-access">
                  <Button size="lg" className="bg-[#1e3a5f] hover:bg-[#2a4a73] text-white px-8 py-6 text-base shadow-xl shadow-[#1e3a5f]/20 transition-all duration-300 hover:-translate-y-0.5">
                    Request Evaluation
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/how-it-works">
                  <Button size="lg" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50 px-8 py-6 text-base">
                    See How It Works
                  </Button>
                </Link>
              </div>
            </div>

            {/* Visual */}
            <div className="hidden lg:block">
              <Card className="bg-white border-slate-200 shadow-2xl">
                <CardContent className="p-8">
                  <h3 className="font-semibold text-[#0f1f2e] mb-6">The Corporate IP Challenge</h3>
                  <div className="space-y-4 mb-6">
                    {[
                      'Budget cycles delay protection of promising ideas',
                      'Upfront commitment required before value is clear',
                      'Multiple projects compete for limited IP allocation',
                      'Strategic flexibility constrained by early commitments'
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="w-2 h-2 bg-slate-400 rounded-full flex-shrink-0" />
                        <span className="text-sm text-slate-600">{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-center space-x-4 my-6">
                    <div className="flex-1 h-0.5 bg-slate-200" />
                    <ArrowLeftRight className="w-6 h-6 text-emerald-500" />
                    <div className="flex-1 h-0.5 bg-slate-200" />
                  </div>
                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="text-sm font-medium text-emerald-800">IpFunders: Upfront cost shifts externally. Investment aligns with progress across the portfolio.</div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Use Case Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f1f2e] mb-4">
              The Corporate IP Challenge
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Great ideas emerge faster than budgets can absorb. IpFunders provides an external path that aligns investment with demonstrated value.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Situation */}
            <Card className="bg-slate-50 border-slate-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-[#0f1f2e] mb-4">The Challenge</h3>
                <ul className="space-y-3">
                  {corporatePageContent.useCase.situation.map((item, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-slate-400 rounded-full mt-2 flex-shrink-0" />
                      <span className="text-slate-600 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* IpFunders Role */}
            <Card className="bg-[#1e3a5f] border-[#1e3a5f]">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-white mb-4">What IpFunders Does</h3>
                <ul className="space-y-3">
                  {corporatePageContent.useCase.ipfundersRole.map((item, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-200 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Outcome */}
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-[#0f1f2e] mb-4">The Outcome</h3>
                <ul className="space-y-3">
                  {corporatePageContent.useCase.outcome.map((item, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f1f2e] mb-4">
              Why Corporate R&D Teams Choose IpFunders
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {corporatePageContent.features.map((feature, idx) => {
              const Icon = iconMap[feature.icon];
              return (
                <Card key={idx} className="bg-white border-slate-200 hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-[#1e3a5f]/10 rounded-xl flex items-center justify-center mb-5">
                      <Icon className="w-6 h-6 text-[#1e3a5f]" />
                    </div>
                    <h3 className="text-lg font-semibold text-[#0f1f2e] mb-2">{feature.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#0f1f2e]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Explore IP Protection With More Flexibility?
          </h2>
          <p className="text-lg text-slate-300 mb-10">
            Let's discuss how IpFunders can complement your internal innovation efforts.
          </p>
          <Link to="/request-access">
            <Button size="lg" className="bg-white text-[#0f1f2e] hover:bg-slate-100 px-10 py-6 text-base font-semibold shadow-xl transition-all duration-300 hover:-translate-y-0.5">
              Request Evaluation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CorporatePage;
